import { getCookie } from '../../common/utils/cookies';
import { markWorkflowEnd } from '../utils/markWorkflowEnd';
import { routeMappings, SessionStore, WorkflowStatus } from '../../constants';
import { Task } from '../interfaces';
import { RR_WITH_HURDLE_EVALUATION_START_TIME, TaskItemStatus } from '../constants';
import { useEffect, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { checkIfRRAppliedButNotFullRR } from '../utils/checkifRRAppliedButNotFullRR';
import { isFrrAppliedOnAllStages } from '../../basicTaskList/utils/basicStageList';
import { useAppSelector } from '../../redux-store/hook';

/**
 * If Workflow is completed (all tasks completed or hurdle is failed)
 * - Mark Workflow end
 * - Redirect to End Page or Hurdle Page
 */
export const useEndWorkflow = (tasks: Task[]): boolean => {
  const [isLoading, setIsLoading] = useState(false);
  const currentWorkflowItem = useAppSelector((state) => state.currentWorkflowItem);
  const history = useHistory();
  const tcPlusFlow = getCookie('TcPlusFlag') === '1';
  const isBasicFlow = getCookie('BasicFlow') === '1';
  const isWorkflowCompleted = tasks.every((task: Task) => task.optional || task.status === TaskItemStatus.COMPLETED);
  let isAnyTaskHurdleFailed = tasks.some((task) => task.status === TaskItemStatus.HURDLE_FAILED);
  const basicRRMsgRead = sessionStorage.getItem(SessionStore.basicRRMsgRead);

  if (tcPlusFlow) isAnyTaskHurdleFailed = tasks.some((i: Task) => i.hurdleStatus === TaskItemStatus.HURDLE_FAILED);

  const rrAppliedButNotFullRR = checkIfRRAppliedButNotFullRR(tasks);
  const frrAppliedOnAllStages = isFrrAppliedOnAllStages(tasks);

  const handleTcPlusWorkflow = useCallback(async (): Promise<void> => {
    const projectSettings = sessionStorage.getItem(SessionStore.projectSettings);
    const showSurvey = projectSettings && JSON.parse(projectSettings).showSurvey;
    /** Exit to basic response reuse flow with hurdle*/
    let isExitToBasicRRFlowWithHurdle = false;
    if (rrAppliedButNotFullRR || frrAppliedOnAllStages) {
      const workflowSession = sessionStorage.getItem(SessionStore.workflowSession);
      const workflowSessionCompleted =
        workflowSession && JSON.parse(workflowSession)?.status === WorkflowStatus.COMPLETED;
      if (!basicRRMsgRead && !(workflowSessionCompleted && isWorkflowCompleted)) isExitToBasicRRFlowWithHurdle = true;
    }
    let firstCompletedTaskWithHurdleNotEvaluated: Task | undefined;
    for (const task of tasks) {
      if (
        (task.hurdleType === 0 || task.hurdleType === 1) &&
        !task.hurdleStatus &&
        task.status === TaskItemStatus.COMPLETED
      ) {
        firstCompletedTaskWithHurdleNotEvaluated = task;
        break;
      }
    }
    if (isAnyTaskHurdleFailed) {
      setIsLoading(true);
      await markWorkflowEnd();
      if (isBasicFlow && showSurvey) {
        history.push(routeMappings['BASIC_SURVEY_PAGE']);
      } else {
        history.push(routeMappings[`${currentWorkflowItem.code}_HURDLE_FAIL`]);
      }
    } else if (firstCompletedTaskWithHurdleNotEvaluated && firstCompletedTaskWithHurdleNotEvaluated.hurdleType === 1) {
      if (isExitToBasicRRFlowWithHurdle) return;
      history.push(
        `${routeMappings['TASK']}/${firstCompletedTaskWithHurdleNotEvaluated.id}${routeMappings['EXPASSES']}/${firstCompletedTaskWithHurdleNotEvaluated.items[0].id}${routeMappings['SCORE_EVALUATION']}`,
      );
    } else if (firstCompletedTaskWithHurdleNotEvaluated && firstCompletedTaskWithHurdleNotEvaluated.hurdleType === 0) {
      if (isExitToBasicRRFlowWithHurdle) return;
      history.push(routeMappings['MANUAL_HURDLE']);
    } else if (isWorkflowCompleted) {
      if (isExitToBasicRRFlowWithHurdle) return;
      setIsLoading(true);
      await markWorkflowEnd();
      if (isBasicFlow && showSurvey) {
        history.push(routeMappings['BASIC_SURVEY_PAGE']);
      } else {
        history.push(routeMappings[`${currentWorkflowItem.code}_END`]);
      }
    }
  }, [
    JSON.stringify(tasks),
    isAnyTaskHurdleFailed,
    isWorkflowCompleted,
    isBasicFlow,
    history,
    currentWorkflowItem.code,
    basicRRMsgRead,
  ]);

  useEffect(() => {
    const redirectToWorkflowEnd = async (): Promise<void> => {
      if (tcPlusFlow) {
        await handleTcPlusWorkflow();
      } else {
        if (isAnyTaskHurdleFailed) {
          setIsLoading(true);
          await markWorkflowEnd();
          if (rrAppliedButNotFullRR)
            setTimeout(() => {
              setIsLoading(false);
              history.push(routeMappings[`${currentWorkflowItem.code}_HURDLE_FAIL`]);
            }, RR_WITH_HURDLE_EVALUATION_START_TIME);
          else {
            setIsLoading(false);
            history.push(routeMappings[`${currentWorkflowItem.code}_HURDLE_FAIL`]);
          }
        } else if (isWorkflowCompleted) {
          setIsLoading(true);
          await markWorkflowEnd();
          setIsLoading(false);
          history.push(routeMappings[`${currentWorkflowItem.code}_END`]);
        }
      }
    };

    const rrCheck = !sessionStorage.getItem('responseReuseInfo') || sessionStorage.getItem('responseReuseAcknowledged');
    if (rrCheck) {
      redirectToWorkflowEnd();
    }
  }, [
    isWorkflowCompleted,
    isAnyTaskHurdleFailed,
    history,
    currentWorkflowItem.code,
    rrAppliedButNotFullRR,
    tcPlusFlow,
    handleTcPlusWorkflow,
    basicRRMsgRead,
  ]);

  return isLoading;
};
